/**
 * useDemoResetFeed — keeps the title-bar demo banner correct on EVERY page.
 *
 * A demo site clears its alerts, events and attendance rows on a timer, and the banner saying
 * so belongs in the title bar of whatever page the user is on: the wipe takes reports and
 * attendance with it, not just the board. But the only cloud page holding a realtime channel
 * to the node is the alert board — so on every other page the banner had nothing to arrive
 * on, and selecting a demo site showed nothing until a board was opened.
 *
 * So the schedule is ASKED FOR the moment a site is selected, through the node's
 * demo-reset read. The board's socket still publishes the same thing while it is mounted (and
 * faster, right after each wipe); the two agree because both come from the node's own timer.
 *
 * Only demo sites are polled after the first answer. On a real site this costs exactly one
 * request per site switch and then nothing at all — the refresh exists to keep `nextResetAt`
 * moving on a page with no socket, which is a question only a demo can answer 'yes' to.
 */
import { useEffect } from 'react';
import { toDemoResetInfo, useSetDemoReset } from '@trinetra/shared';
import { getNodeDemoReset } from '../services/api.service';

/** Matches the node's announce heartbeat — no point asking more often than it can change. */
const REFRESH_MS = 30_000;

export function useDemoResetFeed(selectedNodeId: string | null): void {
  const setDemoReset = useSetDemoReset();

  useEffect(() => {
    // All Sites, or nothing selected: the banner speaks for one site's cycle, so there is
    // nothing it could truthfully say here.
    if (!selectedNodeId) {
      setDemoReset(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const load = async () => {
      try {
        const info = toDemoResetInfo(await getNodeDemoReset(selectedNodeId));
        if (cancelled) return;
        setDemoReset(info);
        // Keep the countdown honest, but only where there is a countdown to keep.
        if (info.enabled && !timer) timer = setInterval(load, REFRESH_MS);
        if (!info.enabled && timer) {
          clearInterval(timer);
          timer = null;
        }
      } catch {
        // An offline node, a cold tunnel, or a node too old to know the route. No banner is
        // the same statement as "not a demo", so failing quietly is the right answer — and
        // the board's socket still publishes it if the user opens one.
      }
    };

    // Clear first: until this site answers, the previous site's countdown must not stand.
    setDemoReset(null);
    load();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [selectedNodeId, setDemoReset]);
}
